import React from 'react';
import { SelectFieldComponent } from '../../../../components/common/SelectFieldComponent';
import { EspecieService } from '../services/especieService';
import { type PresentacionListItem } from '../types/especie';

export interface PresentacionSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  onSelect?: (value: string | number, label: string) => void;
  /** ID of the especie to filter presentaciones */
  idEspecie?: number | string;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter presentaciones */
  filter?: (presentacion: PresentacionListItem) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * PresentacionSelect Component
 *
 * A reusable dropdown component that displays presentaciones filtered by especie.
 * Features:
 * - Automatic data loading based on selected especie
 * - Loading states and error handling
 * - Dynamic filtering by especie ID
 * - Additional custom filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 * - Automatic reset when especie changes
 */
export const PresentacionSelect: React.FC<PresentacionSelectProps> = ({
  value,
  onChange,
  onSelect,
  idEspecie,
  label = 'Presentación',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const [presentaciones, setPresentaciones] = React.useState<PresentacionListItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Fetch presentaciones when idEspecie changes
  const fetchPresentaciones = React.useCallback(async () => {
    if (!idEspecie || idEspecie === '') {
      setPresentaciones([]);
      return;
    }

    try {
      setLoading(true);
      const especieId = typeof idEspecie === 'string' ? parseInt(idEspecie) : idEspecie;

      // Validate that especieId is a valid number
      if (isNaN(especieId) || especieId <= 0) {
        console.warn('Invalid especie ID:', idEspecie);
        setPresentaciones([]);
        return;
      }

      const data = await EspecieService.getPresentacionesByEspecie(especieId);
      setPresentaciones(data);
    } catch (error) {
      console.error('Error fetching presentaciones by especie:', error);
      setPresentaciones([]);
    } finally {
      setLoading(false);
    }
  }, [idEspecie]);

  React.useEffect(() => {
    fetchPresentaciones();
  }, [fetchPresentaciones]);

  // Reset selected value when especie changes
  const prevIdEspecieRef = React.useRef<number | string | undefined>(idEspecie);

  React.useEffect(() => {
    // Only reset if idEspecie actually changed (not on initial mount)
    if (prevIdEspecieRef.current !== undefined && prevIdEspecieRef.current !== idEspecie) {
      if (value && value !== '') {
        // Clear the selected presentacion when especie changes
        onChange('');
      }
    }
    prevIdEspecieRef.current = idEspecie;
  }, [idEspecie, value, onChange]);

  // Filter presentaciones based on props
  const filteredPresentaciones = React.useMemo(() => {
    let filtered = presentaciones;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [presentaciones, filter]);

  // Convert presentaciones to select options
  const options = React.useMemo(() => {
    return filteredPresentaciones.map((presentacion) => ({
      value: presentacion.idPresentacion,
      label: presentacion.descripcion,
    }));
  }, [filteredPresentaciones]);

  // Handle error state
  const displayError = error;

  // Component is disabled if no especie is selected, it's explicitly disabled, or it's loading
  const isDisabled = disabled || loading || !idEspecie || idEspecie === '';

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      onSelect={onSelect}
      options={options}
      error={displayError}
      disabled={isDisabled}
      placeholder={placeholder || (!idEspecie || idEspecie === '' ? 'Selecciona una especie primero' : undefined)}
      loading={loading}
      sx={sx}
    />
  );
};

export default PresentacionSelect;